import { fetchHelpFeedbackSummary, type HelpFeedbackRow } from '@auctisync/api-client';
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

/**
 * Super admin — what readers said about the help articles.
 *
 * WHY THIS PAGE IS THE POINT. The "Was this helpful?" control was deliberately
 * left out of the in-app help centre when it shipped, because there was nowhere
 * for the answer to go: a control that thanks a reader and discards what they
 * said is a ghost feature on the one surface whose job is being trustworthy. It
 * exists now because this page exists. If this page is ever removed, the
 * control should go with it.
 *
 * 🔴 THERE IS NO PERSON IN THIS DATA. `help_article_feedback` records a slug, a
 * tenant, a verdict and a time, and nothing else — the standing rule that buyer
 * data does not enter analytical pools, honoured by the shape of the table
 * rather than by a filter someone has to remember. So this page can show what
 * is wrong with an article and can never show who said so.
 *
 * WORST FIRST, not most-read first. The article most people said did not help
 * them is the one worth rewriting; ordering by traffic buries it under the
 * popular ones. The RPC owns that ordering so a second surface cannot re-sort
 * it into uselessness.
 */

const WINDOWS = [30, 90, 365] as const;

export function SuperHelpFeedback() {
  const [days, setDays] = useState<number>(90);
  const [rows, setRows] = useState<HelpFeedbackRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    const { rows: found, error: err } = await fetchHelpFeedbackSummary(supabase, days);
    if (err) {
      setError(
        /42501|administrator/i.test(err.message)
          ? 'Help feedback is available to platform administrators.'
          : err.message,
      );
      return;
    }
    setRows(found);
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div style={wrapStyle}>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Help feedback
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.3rem' }}>
          What readers pressed at the foot of each article, worst first. No reader is recorded —
          only the article, the house, the verdict and the time.
        </p>
      </header>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => setDays(w)}
            style={{
              padding: '0.4rem 0.8rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
              background: w === days ? 'var(--brand-primary)' : 'var(--surface)',
              color: w === days ? 'var(--brand-primary-fg, #fff)' : 'var(--text)',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {w === 365 ? 'Last year' : `Last ${String(w)} days`}
          </button>
        ))}
      </div>

      {error ? (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      ) : rows === null ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div style={cardStyle}>
          {/* Not "no data" — the honest reading is that nobody has answered
              yet, which is a different thing from the feature being broken. */}
          <p style={{ color: 'var(--text)', margin: 0 }}>
            Nobody has answered “Was this helpful?” in this window.
          </p>
          <p style={{ color: 'var(--text-muted)', margin: '0.4rem 0 0', fontSize: '0.85rem' }}>
            The control sits at the foot of every article in the bidder app and on the help site.
          </p>
        </div>
      ) : (
        <div style={cardStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={thStyle}>Article</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Not helpful</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Helpful</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Last</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.article_slug} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={tdStyle}>
                    <a
                      href={`https://help.auctisync.com/a/${r.article_slug}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
                    >
                      {r.title ?? r.article_slug}
                    </a>
                    {/* The slug, always. A tenant override and the platform
                        article share one, and the slug is what you search for. */}
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      {r.article_slug}
                    </div>
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      textAlign: 'right',
                      fontWeight: 700,
                      color:
                        r.unhelpful_count > 0 ? 'var(--semantic-error, #b91c1c)' : 'var(--text)',
                    }}
                  >
                    {r.unhelpful_count}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{r.helpful_count}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-muted)' }}>
                    {new Date(r.last_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const wrapStyle: React.CSSProperties = {
  padding: '2rem 2.25rem',
  maxWidth: '56rem',
};

const cardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
};

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.4rem',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.4rem',
  color: 'var(--text)',
  verticalAlign: 'top',
};
